import { useTitle } from 'components/utilities/hooks/useTitle';
import { Helmet } from 'react-helmet-async';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { loginAPIUser } from 'services/resources/auth';
import { Link } from 'react-router-dom';
import {
  RESET_PASSWORD_URL,
  SIGNUP_URL,
  TEAM_OVERVIEW_URL,
} from 'constants/urls';
import {
  getLoginErrorMessage,
  isAuth0Configured,
  isEmailValid,
  loginWithAuth0,
  submitDemoLogin,
} from 'utilities/auth';
import { DEMO_USER_EMAIL, DEMO_USER_PASSWORD } from 'constants/emails';
import { useAppContext } from 'services/context';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateIsStaff,
  userDetailsAuthTokenSelector,
} from 'store/slices/userDetailsSlice';
import { useLazyGetUserDetailsQuery } from 'store/apis/userDetailsApi';
import { KEYS } from 'constants/keyboard';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { useLoginPageStyles } from 'components/Login/styles';
import { EMAIL_VALIDATION_MESSAGE } from 'constants/errorMessages';
import { getLocalToken } from 'utilities/token';
import { getWhiteLabelLogo } from 'utilities/whitelabel';
import { setNotificationsCountForTeamOverview } from 'components/TeamOverview/utilities';

export const LoginPage = ({ location, history, isDemo }) => {
  useTitle('HistoWiz - Login');
  const { classes } = useLoginPageStyles();
  const dispatch = useDispatch();
  const { updateIsLoggedIn } = useAppContext();
  const [getUserDetails] = useLazyGetUserDetailsQuery();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [redirectToReferrer, setRedirectToReferrer] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { showError } = useSnackbar();

  const jwtToken = useSelector(userDetailsAuthTokenSelector);
  const localToken = getLocalToken();

  const [logoToUse] = useState(() => {
    try {
      return getWhiteLabelLogo();
    } catch (error) {
      return null;
    }
  });

  const { from } = location.state || {
    from: { pathname: TEAM_OVERVIEW_URL, search: '', hash: '' },
  };

  const updateStaff = (value) => {
    dispatch(updateIsStaff(value));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.hash);
    const error = params.get('#error');
    const errorDescription = params.get('error_description');
    if (error) {
      showError(`Error: ${error}\nDescription: ${errorDescription}`, '', {
        autoHideDuration: 10000,
      });
      setIsLoading(false);

      return;
    }
    setIsLoading(!!(location.pathname === '/' && location.hash));
  }, [location.pathname]);

  useEffect(() => {
    document.title = 'HistoWiz - Login';
    if (isDemo) {
      setNotificationsCountForTeamOverview();
      submitDemoLogin({
        updateStaff,
        getUserDetails,
        setRedirectToReferrer,
        showError,
      });
    }
    if (localToken || jwtToken) {
      setNotificationsCountForTeamOverview();
      history.push(TEAM_OVERVIEW_URL);
    }
  }, [jwtToken, localToken]);

  useEffect(() => {
    if (redirectToReferrer && from?.pathname) {
      const { hash, search } = from;
      const fullPath = `${from.pathname}${search}${hash}`;
      history.push(fullPath);
    }
  }, [redirectToReferrer, from, location]);

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleEmailChange = (event) => {
    setEmailError(false);
    setEmail(event.target.value);
  };

  const handleClickShowPassword = () => {
    setShowPassword((value) => !value);
  };

  const submitGuestSignin = () => {
    const postParams = {
      email: DEMO_USER_EMAIL,
      password: DEMO_USER_PASSWORD,
    };

    loginAPIUser(postParams, updateStaff)
      .then(() => {
        getUserDetails();
        updateIsLoggedIn();
        setRedirectToReferrer(true);
      })
      .catch((error) => {
        showError(getLoginErrorMessage(error));
      });
  };

  const submitSignin = () => {
    const isValidEmail = isEmailValid(email);
    if (!isValidEmail) {
      showError(EMAIL_VALIDATION_MESSAGE);
      setEmailError(true);

      return;
    }

    const postParams = {
      email,
      password,
    };

    loginAPIUser(postParams, updateStaff)
      .then(() => {
        getUserDetails();
        updateIsLoggedIn();

        setRedirectToReferrer(true);
      })
      .catch((error) => {
        showError(getLoginErrorMessage(error));
      });
  };

  const submitAuth0Signin = async () => {
    setIsLoading(true);
    try {
      await loginWithAuth0(email, password);
      getUserDetails();
    } catch (e) {
      showError(e.description);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === KEYS.ENTER) {
      // Enter signs in through Auth0 on the deployed app. Locally there is no Auth0
      // redirect back to localhost, so Enter does what the Sign in button does.
      if (!isAuth0Configured) {
        submitSignin();

        return;
      }
      submitAuth0Signin();
    }
  };

  const disabled = !(email && password) || emailError;

  if (redirectToReferrer) {
    return null;
  }

  return (
    <Paper className={classes.page} elevation={0} square>
      {/* The router falls through to this page for any unmatched path, so noindex here also
          keeps the SPA's unbounded 200 responses out of the index. */}
      <Helmet defer={false}>
        <meta name="robots" content="noindex" />
      </Helmet>
      {isLoading && <CircularProgress className={classes.spinner} />}
      <div className={classes.overlay} />
      <Paper className={classes.card} elevation={0}>
        {logoToUse && (
          <img src={logoToUse} alt="HistoWiz" className={classes.logo} />
        )}
        <Typography variant="h4" className={classes.title}>
          Sign in to HistoWiz
        </Typography>

        <Box
          component="form"
          className={classes.form}
          noValidate
          autoComplete="off"
        >
          <TextField
            label="Email"
            placeholder="Email"
            variant="outlined"
            fullWidth
            type="email"
            value={email}
            onChange={handleEmailChange}
            error={emailError}
            autoComplete="username"
            onKeyPress={handleKeyPress}
            className={classes.field}
          />
          <TextField
            label="Password"
            placeholder="Password"
            variant="outlined"
            fullWidth
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={handlePasswordChange}
            autoComplete="current-password"
            onKeyPress={handleKeyPress}
            className={classes.field}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            fullWidth
            className={classes.signInButton}
            disabled={disabled || isLoading}
            onClick={submitSignin}
          >
            Sign In
          </Button>
        </Box>

        <Link
          to={RESET_PASSWORD_URL}
          className={`${classes.link} ${classes.forgotPasswordLink}`}
        >
          Forgot password?
        </Link>

        <Divider className={classes.divider}>or</Divider>

        <Button
          variant="outlined"
          color="inherit"
          fullWidth
          className={classes.guestButton}
          disabled={isLoading}
          onClick={submitGuestSignin}
        >
          Continue as guest
        </Button>
        <Typography variant="caption" className={classes.guestDescription}>
          Explore sample slides without an account.
        </Typography>

        <Box className={classes.footerRow}>
          <Typography variant="body2" className={classes.footerText}>
            {"Don't have an account?"}
          </Typography>
          <Link to={SIGNUP_URL} className={classes.link}>
            Create an account
          </Link>
        </Box>
      </Paper>
    </Paper>
  );
};
